import React, { useEffect } from "react";
import type { TabType } from "./types";

type TabNavigationProps = {
  activeTab: TabType;
  onTabChange: (tab: TabType) => void;
  hasUnsavedChanges?: boolean;
  isLessonPlan?: boolean;
  hasModuleId?: boolean;
};

export default function TabNavigation({ activeTab, onTabChange, hasUnsavedChanges, isLessonPlan, hasModuleId }: TabNavigationProps) {
  // Keyboard shortcuts: Cmd/Ctrl + 1/2/3/4/5
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey) {
        if (e.key === "1") {
          e.preventDefault();
          onTabChange("overview");
        } else if (e.key === "2") {
          e.preventDefault();
          onTabChange("builder");
        } else if (e.key === "3") {
          e.preventDefault();
          onTabChange("table");
        } else if (e.key === "4") {
          e.preventDefault();
          onTabChange("resources");
        } else if (e.key === "5" && hasModuleId) {
          e.preventDefault();
          onTabChange("issue");
        } else if (e.key === "6" && isLessonPlan) {
          e.preventDefault();
          onTabChange("batches");
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onTabChange, isLessonPlan, hasModuleId]);

  const tabs: Array<{ id: TabType; label: string; icon: string; shortcut: string }> = [
    { id: "overview", label: "Overview", icon: "📋", shortcut: "⌘1" },
    { id: "builder", label: "Builder", icon: "🏗️", shortcut: "⌘2" },
    { id: "table", label: "Table View", icon: "📊", shortcut: "⌘3" },
    { id: "resources", label: "Resources", icon: "📌", shortcut: "⌘4" },
    ...(hasModuleId && !isLessonPlan ? [{ id: "issue" as TabType, label: "Issue", icon: "📤", shortcut: "⌘5" }] : []),
    ...(hasModuleId && isLessonPlan ? [{ id: "batches" as TabType, label: "Batches", icon: "👥", shortcut: "⌘6" }] : []),
  ];

  return (
    <div className="lmb-tab-navigation">
      <div className="lmb-tab-list">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`lmb-tab-button ${activeTab === tab.id ? "active" : ""}`}
            onClick={() => onTabChange(tab.id)}
            type="button"
            title={`${tab.label} (${tab.shortcut})`}
          >
            <span className="lmb-tab-icon">{tab.icon}</span>
            <span className="lmb-tab-label">{tab.label}</span>
            {hasUnsavedChanges && activeTab === tab.id && (
              <span className="lmb-tab-badge">●</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
